/**
 * Editor card-pane + op dispatcher — Cluster F of editor.ts (Phase EB.5).
 *
 * Extracted from editor.ts in Phase EB.5 of the editor-ts-decompose Epic
 * (design doc: docs/design/editor-ts-decompose-plan-260522.md §"Phase EB.5"
 * + §9.5).
 *
 * Cluster F is the highest-coupling cluster (per design doc §3 + §9):
 * it owns op dispatch (`applyPowerEditorOp`), card-pane lifecycle
 * (`mountCardPane`, `rerenderCardList`, `rebuildCardLookups`,
 * `updateCardsPaneTitle`, `updateSelectionStatus`, `updateUndoRedoButtons`),
 * the toast surface (`showEditorToast`), context-action handlers
 * (`_ctxChangeType`, `_ctxDelete`, `_ctxInsert`, `_ctxInsertWithRole`,
 * `_ctxMergeOrGroup`, `dispatchContextAction`, `deleteSelectedStructs`),
 * the reading-order nudge (`nudgeReadingOrder`), and the Save-block UI
 * (`updateSaveBlockUI`).
 *
 * Cross-cluster coupling resolved in EB.5:
 *   - The Phase EB.4 `setInsertFlowCallbackBindings` setter is RETIRED in
 *     EB.5: now that `applyPowerEditorOp`, `rebuildCardLookups`,
 *     `rerenderCardList`, `updateSaveBlockUI`, and `showEditorToast` live
 *     in this module, editor-insert-flow.ts direct-imports them. ESM
 *     handles the resulting cycle (card-pane → insert-flow for
 *     `handleNewInsert` + `canvasBboxToPdf`; insert-flow → card-pane for
 *     the callbacks) because both sides only access the other's exports
 *     lazily at user-gesture time — see design doc §9.5.
 *   - The EB.2 `setOverlayCallbackBindings` setter reduces from 3 bindings
 *     to 1: `applyPowerEditorOp` + `nudgeReadingOrder` become direct
 *     imports in editor-render.ts; only `selectStruct` (which remains in
 *     editor.ts per design doc §9.5) still flows through the setter.
 *   - The EB.3 `setMultipageCallbackBindings` setter reduces from 5
 *     bindings to 1: `dispatchContextAction` now direct-imports from this
 *     module; `handleDragMoveCommit`, `handleResizeCommit`,
 *     `handleDrawCreateCommit` now direct-import from
 *     editor-insert-flow.ts; only `selectStruct` flows through the setter.
 *   - This module introduces NO new setter — `selectStruct` is injected
 *     via direct-import dependency from editor.ts (one-way). Functions
 *     that need it (`mountCardPane` / `rerenderCardList`) accept it as
 *     a parameter passed once at mount time.
 *
 * Per design doc §9.1: `editPaneBody` + `editorToast` are looked up via
 * `document.getElementById` at module scope (re-lookup pattern, sibling
 * of editor-empty-state.ts) rather than being promoted to editor-state.
 */
import { log } from "./log.js";
import { apply as applyIr, IRError } from "./ir.js";
import { commitResult } from "./dispatcher.js";
import { aggregateOps, recordOpApplied } from "./save-flow.js";
import { buildCards, renderCardList, DESCRIPTION_ROLES } from "./cards.js";
import { ROLE_UNDEFINED } from "./role-constants.js";
import { wireSelectionKeyboard, modeFromEvent } from "./multi-select.js";
import { wireContextMenu } from "./context-menu.js";
import { createUndoRedo, mountToast } from "./undo-redo.js";
import { createLinkedViews } from "./linked-views.js";
import { enqueueLocalOp } from "./idb-oplog.js";
import { PDF_US_LETTER } from "./dom-utils.js";
import { updateOverlaySelection } from "./overlays.js";
import { EditorLifecycleState } from "./editor-lifecycle-state.js";
import { SaveBtnState, setSaveBtnState } from "./save-btn-state.js";
import { saveBlockState } from "./insert-flow.js";
import { state, els, setEditorSelection } from "./editor-state.js";
import { renderOverlays, renderOverlaysDebounced, renderPage, } from "./editor-render.js";
// `handleNewInsert` + `canvasBboxToPdf` live in editor-insert-flow.ts;
// importing the *namespace* (not the named bindings) defers value-resolution
// to call time. This breaks the card-pane ↔ insert-flow ESM cycle —
// see editor-insert-flow.ts's parallel `_cp` namespace import for the
// matching shape.
import * as _ifMod from "./editor-insert-flow.js";
let _selectStruct = null;
let _syncMcidToggleUI = null;
function _requireSelectStruct() {
    if (_selectStruct === null) {
        throw new Error("editor-card-pane: selectStruct not installed — "
            + "mountCardPane({selectStruct, syncMcidToggleUI}) must run "
            + "before rerenderCardList()'s onSelect path fires.");
    }
    return _selectStruct;
}
// `syncMcidToggleUI` is a UI sync (idempotent + cheap); when the orchestrator
// hasn't mounted yet (e.g., test harness drives `rebuildCardLookups` directly
// without `mountCardPane`), the no-op is the right behavior — fail-fast would
// trip every seam test for sibling modules that call into `rebuildCardLookups`.
const _NOOP_SYNC_MCID = () => { };
// ─── Toast surface ──────────────────────────────────────────────────────────
const editPaneBody = document.getElementById("pane-editor-body"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
const editorToast = document.getElementById("editor-toast"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
// How long the toast stays visible before auto-dismissing.
const EDITOR_TOAST_MS = 4_000;
let _editorToastTimer = null;
export function showEditorToast(message, type = "info") {
    if (!editorToast)
        return;
    editorToast.dataset.type = type;
    editorToast.classList.remove("hidden");
    if (_editorToastTimer)
        clearTimeout(_editorToastTimer);
    editorToast.textContent = message;
    _editorToastTimer = setTimeout(() => {
        editorToast.classList.add("hidden");
    }, EDITOR_TOAST_MS);
}
// ─── Op dispatcher ──────────────────────────────────────────────────────────
/**
 * Top-level applyOp dispatcher used by the card pane's onOp callback.
 * 1. Validate the op against the current IR (ir.js apply throws on
 *    invalid mutations).
 * 2. Forward to the WASM interop.
 * 3. On WASM failure, surface a toast.
 * 4. On success, push to op log + rebuild the card list.
 */
export async function applyPowerEditorOp(op) {
    if (!state.ir)
        return { ok: false, error: "no IR loaded" };
    // Phase FB.9 (260524): BATCH composite gets its own optimistic-commit
    // branch — folds N sub-ops sequentially through the reducer (one IR
    // commit + one subscriber-notify round), THEN fires per-op WASM
    // serially. The IR mutation has already landed locally — WASM
    // results are consulted only to surface failure toasts. See
    // docs/design/frontend-mv-architecture-260524.md §P.5.
    if (op.kind === "BATCH") {
        return _applyBatchOptimistic(op);
    }
    // Phase FB.8 (260524): apply now returns `{ir, dirtyPages}`. We pre-
    // validate to surface IRError BEFORE the WASM round-trip (and BEFORE
    // committing); the commit + subscriber notify happens via
    // `commitResult(reducerResult)` AFTER WASM acknowledges. The dispatcher's
    // commit path is the load-bearing seam: it rebuilds state.blocksByPage
    // + routes per-page change notifications to viewport-aware subscribers.
    let reducerResult;
    try {
        reducerResult = applyIr(state.ir, op);
    }
    catch (err) {
        const message = (err instanceof IRError) ? err.message : String(err);
        showEditorToast(message, "error");
        return { ok: false, error: message };
    }
    const nextIr = reducerResult.ir;
    // Client-only ops (set-bbox, create-element, delete-element) skip the
    // WASM round-trip: the WASM editor doesn't write `/A[/Layout]/BBox`
    // or restructure the tree on the local SPA — those land server-side
    // at Save time via the existing `add-child` / `remove-child` /
    // `set-bbox` server dispatch
    // (`backend/src/AdaTool.Cli/Pdfs/Model/EditorEditRunner.cs`). Keeping
    // them out of the WASM call prevents a spurious "UnsupportedOpKind"
    // toast on every drag/delete.
    //
    // Pending-insert ops also skip WASM: a freshly-inserted UNDEFINED
    // element has no struct elem in the underlying PDF yet (it lands at
    // Save via add-child), so any WASM dispatch for its structId returns
    // StructIdNotFound and aborts the IR commit. Without this guard, the
    // user picks a type from the popover on a newly-drawn region and
    // sees nothing happen — the op silently failed at the WASM gate
    // before reaching the IR. See bug 6 / commit history.
    // Several Op variants (SetTitle / SetLang / ReorderReading / SetAttestation)
    // are doc-level and carry no structId; the type-guard below narrows to the
    // structId-bearing variants before reaching for op.structId.
    const opStructId = ("structId" in op && typeof op.structId === "string") ? op.structId : null;
    const targetsPendingInsert = opStructId !== null
        && state.pendingInsertStore
        && typeof state.pendingInsertStore.has === "function"
        && state.pendingInsertStore.has(opStructId);
    const isClientOnlyOp = op && (op.kind === "set-bbox" ||
        op.kind === "create-element" ||
        op.kind === "delete-element" ||
        targetsPendingInsert);
    if (!isClientOnlyOp) {
        let wasmResult;
        try {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- WHY: applyOp called from dispatch which only runs after file load (interop non-null)
            wasmResult = await state.interop.applyOp(op);
        }
        catch (err) {
            const message = err && err.message ? err.message : String(err);
            showEditorToast(`PDF write failed: ${message}`, "error");
            return { ok: false, error: message };
        }
        if (!wasmResult || wasmResult.ok === false) {
            const code = wasmResult?.errorCode ?? "WasmOpFailed";
            showEditorToast(`PDF write failed: ${code}`, "error");
            return { ok: false, error: code };
        }
    }
    // Phase FB.8: commit via the dispatcher — assigns state.ir,
    // rebuilds state.blocksByPage (non-doc-level dispatches only), and
    // notifies viewport-intersecting subscribers. Subscribers wired by
    // Phase FB.9+; for now the registry is typically empty and this is a
    // no-op past the state.ir assignment + index rebuild.
    // Phase FB.11 (260524): pass `op` so the dispatcher can route the
    // bbox-cache narrow-invalidation hook for geometry-shaped ops.
    commitResult(reducerResult, op);
    // If a `set-role` resolved an UNDEFINED placeholder, drop the entry from
    // the pendingInsertStore + rewrite the op-log entry to a `create-element`
    // so the wire shape is `add-child` (the server has no UNDEFINED struct
    // elem to mutate; it must be created with the user-picked role from the
    // start). Without this, `pendingInsertStore.size()` stays > 0, the Save
    // button stays in the blocked state forever, and the user sees "type
    // doesn't persist" even though the IR mutation succeeded.
    let opForLog = op;
    if (op.kind === "set-role"
        && typeof op.before === "string"
        && op.before.toUpperCase() === ROLE_UNDEFINED) {
        if (state.pendingInsertStore && typeof state.pendingInsertStore.remove === "function") {
            try {
                state.pendingInsertStore.remove(op.structId);
            }
            catch { /* best-effort: removal failure shouldn't block the IR commit */ }
        }
        // Find the freshly-typed block to recover bbox/page for the wire op.
        const block = (nextIr.blocks || []).find((b) => b.structId === op.structId);
        if (block?.bbox) {
            const bb = block.bbox;
            const bboxArr = [bb.x, bb.y, bb.w, bb.h];
            opForLog = {
                kind: "create-element",
                structId: op.structId,
                parentStructId: null,
                role: op.after,
                bbox: bboxArr,
                page: block.page || 1,
            };
        }
        if (typeof updateSaveBlockUI === "function") {
            try {
                updateSaveBlockUI();
            }
            catch { /* best-effort UI refresh */ }
        }
    }
    state.opLog.push(opForLog);
    // Phase 4: notify the auto-save cadence that an op has been applied.
    // Fire-and-forget; failures are logged inside recordOpApplied/_doFlush
    // and must not interrupt the in-memory op-apply path.
    void recordOpApplied({
        jobId: state.jobId ?? "",
        format: state.structTree?.format?.toLowerCase(),
        opLog: state.opLog,
        pendingInsertStore: state.pendingInsertStore,
    });
    // F7 Stage 5 + Phase FB.10: enqueue into the OplogBuffer. The buffer
    // coalesces up to MAX_OPS (=8) ops OR MAX_AGE_MS (=200) into a single
    // IDB tx; the returned promise resolves when the containing batch is
    // durable on disk (tx.oncomplete). Best-effort — a write failure does
    // not block the in-memory commit (IDB is the resume substrate, not
    // the source of truth). aggregateOps returns the wire form (alt-text +
    // bbox normalized); the SPA op may be dropped (returns null) for ops
    // that don't map to wire ops, in which case nothing is persisted.
    // Per §P.6 of docs/design/frontend-mv-architecture-260524.md.
    if (state.editorDb && state.jobId) {
        const wireOps = aggregateOps([op]);
        const wireOp = wireOps.length > 0 ? wireOps[0] : null;
        if (wireOp) {
            const seq = state._idbLastOpSeq + 1;
            const record = {
                jobId: state.jobId,
                sessionId: state.currentSessionId,
                opSeq: seq,
                ts: new Date().toISOString(),
                ...wireOp,
                synced: false,
            };
            state._idbLastOpSeq = seq;
            /* eslint-disable promise/prefer-await-to-then -- fire-and-forget IDB write; containing function is sync */
            enqueueLocalOp(state.editorDb, record).catch((err) => {
                log.warn("[editor] IDB enqueueLocalOp failed:", err);
            });
            /* eslint-enable promise/prefer-await-to-then */
        }
    }
    // Rebuild the card list so the new value (alt text, role change, reorder)
    // is reflected in the card pane. Cheap — buildCards is O(N) over
    // top-level blocks; renderCardList batches via innerHTML.
    state.cards = buildCards(state.ir);
    rebuildCardLookups();
    rerenderCardList();
    // Re-render the overlays so reading-order badges + alt-text tooltips
    // pick up the new state. Uses the debounced path to coalesce rapid edits.
    renderOverlaysDebounced();
    return { ok: true };
}
/**
 * BATCH composite-action handler — Phase FB.9 of
 * frontend-mv-architecture-260524.md §P.5.
 *
 * Optimistic-commit semantics distinct from the single-op path:
 *   1. Pre-validate the WHOLE BATCH via `applyIr(state.ir, batchOp)`. If
 *      any sub-op throws IRError, the BATCH is rejected before commit —
 *      atomic boundary, no partial state visible to subscribers.
 *   2. Commit ONCE via `commitResult(reducerResult)`. The IR mutation
 *      has landed locally; subscribers fire with the union dirtyPages.
 *   3. Walk sub-ops serially:
 *      - Non-client-only sub-ops fire WASM round-trips (serialized
 *        matching the pre-FB.9 IIFE behavior). WASM failure surfaces a
 *        toast but does NOT roll back the optimistic commit — undo is
 *        the user-driven safety net.
 *      - Each sub-op pushes to opLog (with UNDEFINED → create-element
 *        rewrite per the existing single-op rule) and the IDB oplog.
 *   4. Single card-list rebuild + overlay refresh at the end (one
 *      O(N-blocks) buildCards call instead of N).
 *
 * Why this is the load-bearing FB.9 fix: dispatching N separate
 * single-op `set-role`s through the for-loop in `_ctxChangeType` read
 * the SAME starting `state.ir` per iteration; the last to resolve
 * clobbered earlier mutations. BATCH folds the N sub-ops sequentially
 * against ONE starting IR, producing one consistent commit.
 */
async function _applyBatchOptimistic(batchOp) {
    if (!state.ir)
        return { ok: false, error: "no IR loaded" };
    if (batchOp.ops.length === 0)
        return { ok: true };
    // Pre-validate the WHOLE batch — surfaces IRError for any sub-op
    // before we touch state.ir. The fold is pure (no mutation).
    let reducerResult;
    try {
        reducerResult = applyIr(state.ir, batchOp);
    }
    catch (err) {
        const message = (err instanceof IRError) ? err.message : String(err);
        showEditorToast(message, "error");
        return { ok: false, error: message };
    }
    // Optimistic commit — IR mutation lands BEFORE any WASM round-trip
    // fires. Single commitResult call → single subscriber-notify round.
    // Phase FB.11 (260524): pass the BATCH op so the dispatcher's
    // narrow-invalidation hook can recurse into sub-ops and evict the
    // bbox-cache entries for geometry-touched pages only.
    commitResult(reducerResult, batchOp);
    // Walk sub-ops serially: WASM round-trip per non-client-only op,
    // oplog + IDB push per op. WASM failure is surfaced as a toast but
    // does NOT roll back the optimistic commit (undo is the safety net).
    let firstFailure = null;
    for (const subOp of batchOp.ops) {
        const subStructId = ("structId" in subOp && typeof subOp.structId === "string") ? subOp.structId : null;
        const targetsPendingInsert = subStructId !== null
            && state.pendingInsertStore
            && typeof state.pendingInsertStore.has === "function"
            && state.pendingInsertStore.has(subStructId);
        const isClientOnly = subOp.kind === "set-bbox"
            || subOp.kind === "create-element"
            || subOp.kind === "delete-element"
            || targetsPendingInsert;
        if (!isClientOnly) {
            let wasmResult;
            try {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- WHY: batch applyOp called from dispatch path after file load (interop non-null)
                wasmResult = await state.interop.applyOp(subOp);
            }
            catch (err) {
                const message = err && err.message ? err.message : String(err);
                if (firstFailure === null) {
                    firstFailure = message;
                    showEditorToast(`PDF write failed: ${message}`, "error");
                }
                continue; // continue with the rest of the batch — optimistic commit stands
            }
            if (!wasmResult || wasmResult.ok === false) {
                const code = wasmResult?.errorCode ?? "WasmOpFailed";
                if (firstFailure === null) {
                    firstFailure = code;
                    showEditorToast(`PDF write failed: ${code}`, "error");
                }
                continue;
            }
        }
        // UNDEFINED-placeholder rewrite (same as single-op path): if a
        // sub-op is set-role and before is ROLE_UNDEFINED, drop from
        // pendingInsertStore and rewrite the oplog entry to
        // create-element.
        let subForLog = subOp;
        if (subOp.kind === "set-role"
            && typeof subOp.before === "string"
            && subOp.before.toUpperCase() === ROLE_UNDEFINED) {
            if (state.pendingInsertStore && typeof state.pendingInsertStore.remove === "function") {
                try {
                    state.pendingInsertStore.remove(subOp.structId);
                }
                catch { /* best-effort: removal failure shouldn't block the IR commit */ }
            }
            const block = (state.ir.blocks ?? []).find((b) => b.structId === subOp.structId);
            if (block?.bbox) {
                const bb = block.bbox;
                const bboxArr = [bb.x, bb.y, bb.w, bb.h];
                subForLog = {
                    kind: "create-element",
                    structId: subOp.structId,
                    parentStructId: null,
                    role: subOp.after,
                    bbox: bboxArr,
                    page: block.page ?? 1,
                };
            }
            if (typeof updateSaveBlockUI === "function") {
                try {
                    updateSaveBlockUI();
                }
                catch { /* best-effort UI refresh */ }
            }
        }
        state.opLog.push(subForLog);
        // IDB oplog — best-effort per sub-op (Phase FB.10: routes through
        // OplogBuffer for batch coalescing; see the parallel comment in
        // the non-batch dispatch site above).
        if (state.editorDb && state.jobId) {
            const wireOps = aggregateOps([subOp]);
            const wireOp = wireOps.length > 0 ? wireOps[0] : null;
            if (wireOp) {
                const seq = state._idbLastOpSeq + 1;
                const record = {
                    jobId: state.jobId,
                    sessionId: state.currentSessionId,
                    opSeq: seq,
                    ts: new Date().toISOString(),
                    ...wireOp,
                    synced: false,
                };
                state._idbLastOpSeq = seq;
                /* eslint-disable promise/prefer-await-to-then -- fire-and-forget IDB write; loop body is async but per-op IDB persist is best-effort */
                enqueueLocalOp(state.editorDb, record).catch((err) => {
                    log.warn("[editor] IDB enqueueLocalOp failed:", err);
                });
                /* eslint-enable promise/prefer-await-to-then */
            }
        }
    }
    // Single card-list rebuild + overlay refresh after the whole batch.
    state.cards = buildCards(state.ir);
    rebuildCardLookups();
    rerenderCardList();
    renderOverlaysDebounced();
    if (firstFailure !== null) {
        return { ok: false, error: firstFailure };
    }
    return { ok: true };
}
// ─── Card-pane mount ────────────────────────────────────────────────────────
/**
 * Mount the card-list pane inside the edit pane body. Idempotent — re-mount
 * on a fresh PDF reuses the existing card-list container.
 *
 * @param deps  orchestrator-owned callbacks (selectStruct, syncMcidToggleUI).
 *     See `CardPaneDeps`. Pinned on the first mount; subsequent mounts
 *     overwrite the prior bindings (testable shape).
 */
export function mountCardPane(deps) {
    _selectStruct = deps.selectStruct;
    _syncMcidToggleUI = deps.syncMcidToggleUI;
    if (!editPaneBody || !state.ir)
        return;
    let cardListEl = editPaneBody.querySelector(".editor-card-list");
    if (!cardListEl) {
        cardListEl = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        cardListEl.className = "editor-card-list";
        editPaneBody.appendChild(cardListEl);
    }
    state.cards = buildCards(state.ir);
    rebuildCardLookups();
    // Linked-views handle. Recreated on each mount so a fresh PDF starts clean.
    state.linkedViewsCtrl = createLinkedViews({
        cardListEl,
        overlayContainerEl: els.overlay,
        onPdfPageScroll: (page) => {
            if (!page || !state.pdfDoc)
                return;
            // Multi-page-scroll path — preferred when the manager is present.
            // The renderPage() fallback (below) is only reached during unit
            // tests or before mountMultiPageScroll has fired; it no-ops when
            // the requested page is already state.currentPage, which is a
            // problem when the user clicks a card whose page indicator already
            // reads the right value but the viewport isn't scrolled there.
            // scrollToPage() always brings the page to the top of the
            // viewport regardless of current scroll position.
            if (state.multiPageMgr && typeof state.multiPageMgr.scrollToPage === "function") {
                void state.multiPageMgr.scrollToPage(page);
                return;
            }
            if (page !== state.currentPage) {
                // eslint-disable-next-line promise/prefer-await-to-then -- fire-and-forget in sync event callback
                void renderPage(page).catch(() => { });
            }
        },
    });
    state.linkedViewsCtrl.setRenderHook((selectedSet) => {
        setEditorSelection(selectedSet);
        if (typeof updateOverlaySelection === "function") {
            // Pass the full set so multi-selected overlays light up.
            updateOverlaySelection(els.overlay, selectedSet);
        }
        // Keep statusbar selection counter in sync.
        updateSelectionStatus();
    });
    // Mount keyboard, context menu, and undo/redo. Detach prior mounts so
    // re-mount on a fresh PDF doesn't double-fire.
    if (state.multiSelectDetach) {
        try {
            state.multiSelectDetach();
        }
        catch { /* best-effort; we're remounting */ }
        state.multiSelectDetach = null;
    }
    if (state.contextMenuDetach) {
        try {
            state.contextMenuDetach();
        }
        catch { /* best-effort */ }
        state.contextMenuDetach = null;
    }
    state.toast ??= mountToast({ document });
    if (!state.undoRedo) {
        const inner = createUndoRedo({ showToast: state.toast });
        // Wrap so toolbar button-disabled state stays in sync with
        // canUndo() / canRedo() across every push / undo / redo.
        state.undoRedo = {
            push(record) { inner.push(record); updateUndoRedoButtons(); },
            undo() { const r = inner.undo(); updateUndoRedoButtons(); return r; },
            redo() { const r = inner.redo(); updateUndoRedoButtons(); return r; },
            clear() { inner.clear(); updateUndoRedoButtons(); },
            canUndo: () => inner.canUndo(),
            canRedo: () => inner.canRedo(),
            sizes: () => inner.sizes(),
        };
    }
    // Reset the undo stack on each fresh PDF so cross-document op records
    // don't surface confusingly.
    state.undoRedo.clear();
    // Toolbar Undo/Redo button listeners are wired ONCE at module init by
    // _bootEditorToolbar(); no per-load wiring here.
    updateUndoRedoButtons();
    state.multiSelectDetach = wireSelectionKeyboard({
        document,
        getLinkedViews: () => state.linkedViewsCtrl,
        getOrderedIds: () => state.linkedViewsCtrl ? state.linkedViewsCtrl.orderedCardIds() : [],
        getSelected: () => state.selectedStructIds,
        onDeleteSelection: () => void deleteSelectedStructs({ confirm: true }),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- WHY: undoRedo is mounted before mountCardPane returns; guaranteed non-null by initialization order
        onUndo: () => state.undoRedo.undo(),
        onRedo: () => state.undoRedo.redo(),
    });
    // overlayContainerEl is null — per-page overlay contextmenu wiring lives in
    // _wirePageControllers (openContextMenu on each active per-page overlay).
    // The hidden legacy #editor-pdf-overlay (els.overlay) must not be wired here.
    state.contextMenuDetach = wireContextMenu({
        cardListEl,
        overlayContainerEl: null,
        /* eslint-disable @typescript-eslint/no-unsafe-return -- WHY: state fields (linkedViewsCtrl, selectedStructIds, overlayRoleMap, cardByStructId) typed as any in EditorState; full typing is a separate effort */
        getLinkedViews: () => state.linkedViewsCtrl,
        getSelected: () => state.selectedStructIds || new Set(),
        getOrderedIds: () => state.linkedViewsCtrl ? state.linkedViewsCtrl.orderedCardIds() : [],
        getRoleByStructId: () => state.overlayRoleMap ?? new Map(),
        getCardByStructId: () => state.cardByStructId || new Map(),
        /* eslint-enable @typescript-eslint/no-unsafe-return */
        onAction: (action, payload) => { void dispatchContextAction(action, payload); },
    });
    // Click-outside-an-expanded-card → collapse. Mockup ref:
    // tmp/editor-mockup-card-gui-260501.html §"Click outside → collapse"
    // (line ~2125). The Esc key already collapses via cards.js fields
    // keydown handler; this listener gives the same affordance via mouse.
    if (state.collapseOnOutsideDetach) {
        try {
            state.collapseOnOutsideDetach();
        }
        catch { /* best-effort */ }
        state.collapseOnOutsideDetach = null;
    }
    const onOutsideMousedown = (ev) => {
        if (!state.expandedIds || state.expandedIds.size === 0)
            return;
        const t = (ev?.target);
        if (!t)
            return;
        // Don't collapse when the click lands inside any popover, modal,
        // toast, or context menu — those overlays can outlive the click.
        if (t.closest && (t.closest(".editor-type-popover") ||
            t.closest(".editor-issue-popover") ||
            t.closest(".editor-modal-backdrop") ||
            t.closest(".editor-context-menu") ||
            t.closest(".editor-toast"))) {
            return;
        }
        const insideCard = t?.closest(".editor-card");
        // No card under cursor → collapse all.
        if (!insideCard) {
            state.expandedIds.clear();
            rerenderCardList();
            return;
        }
        // Click on a card that ISN'T currently expanded → collapse the
        // currently expanded one. Clicks inside the expanded card itself
        // are no-op'd.
        const id = insideCard instanceof HTMLElement ? insideCard.dataset.structId : null;
        if (id && !state.expandedIds.has(id)) {
            state.expandedIds.clear();
            rerenderCardList();
        }
    };
    document.addEventListener("mousedown", onOutsideMousedown, true); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    state.collapseOnOutsideDetach = () => document.removeEventListener("mousedown", onOutsideMousedown, true); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    rerenderCardList();
}
// ─── Context-action handlers ────────────────────────────────────────────────
//
// Dispatch a bulk-op from the right-click context menu. Each action maps to
// one or more `applyPowerEditorOp` calls (with one undo/redo record bundling
// them together so Cmd-Z reverts the whole bulk action atomically).
//
// Restructurer ops (FuseSiblings / Demote / RemoveChild) are not yet on the
// WASM surface; we dispatch them as best we can with the existing op set,
// recording inverse functions for the client-side undo stack so the user
// sees the action complete locally.
// eslint-disable-next-line @typescript-eslint/require-await -- WHY: ContextHandler requires Promise<void>; no await needed today but async keeps the return type consistent with the handler table contract
async function _ctxChangeType(sel, payload) {
    const newRole = payload;
    if (!newRole)
        return;
    // Capture old roles for undo.
    const beforeRoles = new Map();
    for (const id of sel) {
        const c = state.cardByStructId?.get(id);
        if (c)
            beforeRoles.set(id, c.roleToken ?? c.role);
    }
    if (beforeRoles.size === 0)
        return;
    // Phase FB.9 (260524): retired the 260523 async-IIFE-wrap. The
    // pre-FB.9 multi-select change-type for-loop awaited a WASM
    // round-trip per iteration; every iteration computed its nextIr
    // from the SAME starting state.ir and the last to resolve
    // clobbered earlier mutations. The IIFE serialized the awaits but
    // was a surgical patch at one site. BATCH retires the race CLASS:
    // one dispatch folds N set-role sub-ops sequentially against ONE
    // starting IR, one IR commit lands, then per-sub-op WASM
    // round-trips fire serially after. See §P.5 of
    // docs/design/frontend-mv-architecture-260524.md.
    const forwardOps = Array.from(beforeRoles, ([id, oldRole]) => ({
        kind: "set-role",
        structId: id,
        before: oldRole,
        after: newRole,
    }));
    const inverseOps = Array.from(beforeRoles, ([id, oldRole]) => ({
        kind: "set-role",
        structId: id,
        before: newRole,
        after: oldRole,
    }));
    const applyForward = () => {
        void applyPowerEditorOp({ kind: "BATCH", ops: forwardOps });
    };
    const applyInverse = () => {
        void applyPowerEditorOp({ kind: "BATCH", ops: inverseOps });
    };
    applyForward();
    if (state.undoRedo) {
        state.undoRedo.push({
            doFn: applyForward,
            undoFn: applyInverse,
            label: `change type to ${newRole}`,
        });
    }
}
async function _ctxDelete() {
    // delete-confirm has already happened in context-menu.js if needed.
    await deleteSelectedStructs({ confirm: false });
}
async function _ctxInsert(payload) {
    // Right-click "Insert other (drag to draw)" — default-sized box at the
    // click point (or page center when payload.clickPt is missing). Falls
    // through to the UNDEFINED + popover flow (user picks the role next).
    const { defaultInsertSize: defaultSz, nextEditorInsertId } = await import("./insert-flow.js"); // noqa: no-inline-imports — code-split: defer loading insert-flow.js until actually needed to reduce initial bundle size
    const pageSize = (() => {
        const desc = state.pageDescriptors[state.currentPage - 1];
        if (desc?.frame) {
            return { width: desc.frame.offsetWidth ?? PDF_US_LETTER.width, height: desc.frame.offsetHeight ?? PDF_US_LETTER.height };
        }
        return PDF_US_LETTER;
    })();
    const clickPt = (payload?.clickPt) ?? { x: pageSize.width / 2, y: pageSize.height / 2 };
    const bbox = defaultSz(pageSize, clickPt);
    _ifMod.handleNewInsert({
        editorId: nextEditorInsertId(),
        page: state.currentPage,
        bbox,
        pdfBbox: null,
        anchorStructId: null,
    });
}
async function _ctxInsertWithRole(payload) {
    // Right-click "Insert paragraph" / "Insert heading H2" / etc. —
    // the role is pre-picked, so we bypass the UNDEFINED + popover flow
    // and create the new element immediately at the click point.
    if (!payload?.role)
        return;
    const { defaultInsertSize: defaultSz, nextEditorInsertId } = await import("./insert-flow.js"); // noqa: no-inline-imports — code-split: defer loading insert-flow.js until actually needed to reduce initial bundle size
    const pageSize = (() => {
        const desc = state.pageDescriptors[state.currentPage - 1];
        if (desc?.frame) {
            return { width: desc.frame.offsetWidth ?? PDF_US_LETTER.width, height: desc.frame.offsetHeight ?? PDF_US_LETTER.height };
        }
        return PDF_US_LETTER;
    })();
    const clickPt = payload.clickPt ?? { x: pageSize.width / 2, y: pageSize.height / 2 };
    const canvasBbox = defaultSz(pageSize, clickPt);
    // Convert the canvas-px bbox to a PDF user-space bbox so the IR carries
    // the right coords for the overlay refresh + the wire op envelope.
    const pdfBbox = _ifMod.canvasBboxToPdf(canvasBbox, state.currentPage);
    const role = String(payload.role);
    const structId = nextEditorInsertId();
    function applyForward() {
        void applyPowerEditorOp({
            kind: "create-element",
            structId,
            parentStructId: "S",
            role,
            bbox: pdfBbox,
            page: state.currentPage,
        });
    }
    // Phase FB.9 (260524): undo of insert-with-role routes through
    // applyPowerEditorOp with a delete-element op so the dispatcher
    // commits, rebuilds blocksByPage, and rebuilds the card list +
    // overlays via the post-commit path. Per §P.5 of
    // docs/design/frontend-mv-architecture-260524.md (dispatch monopoly).
    function applyInverse() {
        if (!state.ir || !Array.isArray(state.ir.blocks))
            return;
        void applyPowerEditorOp({ kind: "delete-element", structId });
    }
    applyForward();
    if (state.undoRedo) {
        state.undoRedo.push({
            doFn: applyForward,
            undoFn: applyInverse,
            label: `insert ${role}`,
        });
    }
    // Select the freshly created element so the user can immediately edit
    // its content / fine-tune its bbox via drag-to-move.
    if (state.linkedViewsCtrl)
        state.linkedViewsCtrl.selectStructId(structId, "single");
    // Re-render overlays so the new bbox is visible.
    // eslint-disable-next-line promise/prefer-await-to-then -- fire-and-forget in sync context-menu handler
    renderOverlays().catch(() => { });
}
// eslint-disable-next-line @typescript-eslint/require-await -- WHY: ContextHandler requires Promise<void>; no await needed (stub impl, server wiring pending)
async function _ctxMergeOrGroup(action) {
    // FuseSiblings / Demote restructurers aren't on the WASM surface yet;
    // surface a placeholder toast so the user sees their click registered.
    if (state.toast) {
        state.toast(`${action[0].toUpperCase() + action.slice(1)}: queued (server wiring pending)`); // WHY: action is always a non-empty key string
    }
}
const CONTEXT_ACTIONS = {
    "change-type": async (sel, payload) => _ctxChangeType(sel, payload),
    "delete": async () => _ctxDelete(),
    "insert": async (_s, payload) => _ctxInsert(payload),
    "insert-with-role": async (_s, payload) => _ctxInsertWithRole(payload),
    "merge": async (_sel, _payload, action) => _ctxMergeOrGroup(action),
    "group": async (_sel, _payload, action) => _ctxMergeOrGroup(action),
};
export async function dispatchContextAction(action, payload) {
    const sel = state.selectedStructIds ? new Set(state.selectedStructIds) : new Set();
    // Insert actions are valid with or without a current selection.
    const isInsertAction = action === "insert" || action === "insert-with-role";
    if (sel.size === 0 && !isInsertAction)
        return;
    const handler = CONTEXT_ACTIONS[action];
    if (!handler)
        throw new Error(`Unknown context action: ${action}`);
    return handler(sel, payload, action);
}
/**
 * Delete every currently-selected struct element. Each selected card emits
 * a `delete-element` op to `applyPowerEditorOp`, which:
 *  - mutates the local IR (filters the block out — see ir.js apply()
 *    case "delete-element"),
 *  - pushes the op to `state.opLog` so it survives Save (the wire layer
 *    in `save-flow.js opToWireOp` translates it to the server's
 *    `remove-child` op; the C# dispatcher in `EditorEditRunner.cs`
 *    routes through `PdfStructTreeKidsMutator.RemoveChild`).
 *
 * The undo-redo bundle wraps the whole multi-delete as a single Cmd-Z
 * frame (so undoing a 3-card delete restores all 3). The bundled frame
 * does NOT walk the oplog itself — the oplog and the keyboard-undo
 * stack are parallel substrates today; future work may merge them.
 *
 * If `opts.confirm` and any selected card has authored content (alt text on
 * a Figure), we prompt before proceeding. The right-click path passes
 * `confirm:false` because it has already prompted.
 */
export async function deleteSelectedStructs(_opts = {}) {
    const sel = state.selectedStructIds ? Array.from(state.selectedStructIds) : [];
    if (sel.length === 0)
        return;
    // Delete is reversible via Undo (Cmd-Z or the toolbar Undo button) for
    // the lifetime of the editor session: deleteSelectedStructs pushes a
    // record onto state.undoRedo whose undoFn re-splices the captured blocks
    // back into the IR. opts.confirm is preserved so callers (e.g., the
    // right-click menu) can request a soft-confirm, but the default UX is
    // no popup — undo is the safety net per editor-philosophy.md.
    // Capture removed blocks (with their position) for undo.
    const blocksBefore = state.ir && Array.isArray(state.ir.blocks) ? state.ir.blocks.slice() : [];
    const removed = [];
    for (let i = 0; i < blocksBefore.length; i++) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- WHY: i < blocksBefore.length guarantees defined
        if (sel.includes(blocksBefore[i].structId)) {
            removed.push({ idx: i, block: blocksBefore[i] });
        }
    }
    const summaryRole = (() => {
        if (sel.length === 1) {
            const c = state.cardByStructId?.get(sel[0]); // WHY: sel.length === 1 check above
            return c ? (c.roleToken ?? c.role) : "element";
        }
        return `${sel.length} elements`;
    })();
    async function applyDeletes() {
        // Emit one delete-element op per selected struct. Each call routes
        // through applyPowerEditorOp, which runs the IR reducer + pushes to
        // the oplog. The captured block snapshot rides on the op's `before`
        // field so oplog.inverseOf can reconstitute the block on undo when
        // a future revision wires create-element to be the literal inverse.
        const byId = new Map();
        for (const r of removed)
            byId.set(r.block.structId, r.block);
        for (const id of sel) {
            const beforeBlock = byId.get(id) ?? null;
            await applyPowerEditorOp({
                kind: "delete-element",
                structId: id,
                before: beforeBlock,
            });
        }
    }
    function spliceIn() {
        if (!state.ir || !Array.isArray(state.ir.blocks))
            return;
        const newBlocks = state.ir.blocks.slice();
        // Reinsert in original index order (ascending) so positions are stable.
        const sorted = removed.slice().sort((a, b) => a.idx - b.idx);
        for (const { idx, block } of sorted) {
            const at = Math.min(idx, newBlocks.length);
            newBlocks.splice(at, 0, block);
        }
        // Position-aware reinsertion preserves the deleted blocks' original
        // index within state.ir.blocks (sorted ascending + spliced in). The
        // existing createElementReducer only APPENDS — an N×create-element
        // BATCH would lose positional context. [DESIGN] follow-up: extend
        // create-element with an `insertAt: number` field so undo-of-delete
        // becomes a pure dispatch. Until then, the direct write below carries
        // a same-line noqa per the lint contract.
        state.ir = { ...state.ir, blocks: newBlocks }; // noqa: editor-dispatch-monopoly — position-aware reinsert; [DESIGN] follow-up tracks insertAt extension // noqa: editor-oplog-monopoly — undo-of-delete is an inverse-op application: the forward delete-element op was already logged, so no new op is needed here
        state.cards = buildCards(state.ir);
        rebuildCardLookups();
        rerenderCardList();
        renderOverlaysDebounced();
    }
    await applyDeletes();
    if (state.undoRedo) {
        state.undoRedo.push({
            // eslint-disable-next-line promise/prefer-await-to-then -- fire-and-forget in sync undo/redo callback
            doFn: () => { applyDeletes().catch(() => { }); },
            undoFn: spliceIn,
            label: `deleted ${summaryRole}`,
        });
    }
    // Clear selection — the deleted elements are gone.
    if (state.linkedViewsCtrl)
        state.linkedViewsCtrl.clearSelection();
}
// ─── Card-lookups + status surfaces ─────────────────────────────────────────
export function rebuildCardLookups() {
    state.cardByStructId = new Map();
    state.readingOrderByStructId = new Map();
    state.structIdsWithLongDescNext = new Set();
    let i = 0;
    for (const c of state.cards) {
        if (!c)
            continue;
        i += 1;
        state.cardByStructId.set(c.structId, c);
        const idx = (typeof c.readingOrderIndex === "number" && c.readingOrderIndex > 0)
            ? c.readingOrderIndex : i;
        state.readingOrderByStructId.set(c.structId, idx);
    }
    // Mark every structId whose immediately-following block in reading order
    // is an AdaTool-stamped long-description paragraph. The overlay renders
    // the predecessor's reading-order chip as "X, X+1" — surfacing the
    // figure ↔ long-desc association at a glance. The flag lives on IR
    // blocks (state.ir.blocks), not cards, because the cards view
    // intentionally renders the long-desc as a normal paragraph card with
    // NO badge per the 260513 design.
    const blocks = (state.ir && Array.isArray(state.ir.blocks)) ? state.ir.blocks : [];
    for (let k = 0; k + 1 < blocks.length; k++) {
        const current = blocks[k];
        const next = blocks[k + 1];
        if (!current || !next)
            continue;
        if (next.insertedByDocableLongDescription === true) {
            state.structIdsWithLongDescNext.add(current.structId);
        }
    }
    // Refresh MCID toggle disabled-state in case ir.mcids appeared / vanished
    // after a reload or fallback. Idempotent and cheap.
    (_syncMcidToggleUI ?? _NOOP_SYNC_MCID)();
}
export function updateCardsPaneTitle() {
    if (!els.cardsPaneTitle)
        return;
    if (!state.ir || !Array.isArray(state.ir.blocks)) {
        els.cardsPaneTitle.textContent = "Structure";
        // No-IR state: clear status counts.
        if (els.statusElemCount)
            els.statusElemCount.textContent = "—";
        if (els.statusIssueCount) {
            els.statusIssueCount.textContent = "—";
            els.statusIssueCount.classList.remove("has-issues");
        }
        return;
    }
    const blocks = state.ir.blocks;
    const elemCount = blocks.length;
    // Total issue count: server-flagged issues PLUS Figure-with-no-alt PLUS
    // Formula/Table-with-no-description PLUS UNDEFINED inserts. Reuses the
    // canonical DESCRIPTION_ROLES source of truth (rule #11 — no hardcoded copy)
    // so this count stays in lockstep with the cards-pane issue-badge surface.
    let issueCount = 0;
    for (const b of blocks) {
        if (!b)
            continue;
        if (b.issue) {
            issueCount++;
            continue;
        }
        if (b.role === ROLE_UNDEFINED) {
            issueCount++;
            continue;
        }
        const role = b.kind === "Heading" ? `H${b.level ?? 1}` : (b.kind === "Paragraph" ? "Paragraph" : b.kind);
        const bFig = b.kind === "Figure" ? b : null;
        if (DESCRIPTION_ROLES.has(role) && !bFig?.decorative && !(bFig?.alt ?? "").trim()) {
            issueCount++;
        }
    }
    const elemWord = `${elemCount} element${elemCount === 1 ? "" : "s"}`;
    const issueWord = `${issueCount} issue${issueCount === 1 ? "" : "s"}`;
    els.cardsPaneTitle.textContent = `Structure — ${elemWord} · ${issueWord}`;
    // Mirror counts into the statusbar so the user can see them without
    // looking at the cards-pane header.
    if (els.statusElemCount)
        els.statusElemCount.textContent = elemWord;
    if (els.statusIssueCount) {
        els.statusIssueCount.textContent = issueWord;
        els.statusIssueCount.classList.toggle("has-issues", issueCount > 0);
    }
}
/** Update the statusbar selection counter ("N selected") on selection change. */
export function updateSelectionStatus() {
    if (!els.statusSelCount)
        return;
    const n = state.selectedStructIds ? state.selectedStructIds.size : 0;
    els.statusSelCount.textContent = n > 1 ? `${n} selected` : "";
}
/** Sync toolbar Undo / Redo button-disabled state with the controller's
 *  canUndo() / canRedo() flags. Called after every push / undo / redo. */
export function updateUndoRedoButtons() {
    if (!els.undoBtn || !els.redoBtn)
        return;
    const canU = !!(state.undoRedo?.canUndo?.());
    const canR = !!(state.undoRedo?.canRedo?.());
    els.undoBtn.disabled = !canU;
    els.redoBtn.disabled = !canR;
}
// P0/P1 focus-preservation (Epic editor-open-load-state-coherence-260813 Phase 3).
// renderCardList() rebuilds `.editor-card-list` wholesale via innerHTML. When a
// card-derived op fires WHILE the user is typing in a field inside the list — the
// debounced `set-alt-text` op from a description textarea is the canonical case
// (cards.ts) — that rebuild destroys the very node the caret lives in, which both
// (P1) yanks focus out of the field the user is editing AND (P0) drops any
// characters typed after the debounced op snapshotted the value. So: skip the
// destructive rebuild while an editable card-list field is focused, and re-check
// once focus leaves it. The visible card already shows the user's value (the field
// IS the value), so deferring is loss-free; structural ops (add/remove/set-role via
// <select>/reorder via buttons) move focus off any text field first, so this guard
// never fires for them and they rebuild immediately as before.
let _cardListReconcileArmed = false;
function _focusedEditableInCardList(cardListEl) {
    if (!cardListEl)
        return null;
    const ae = document.activeElement; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (!ae)
        return null;
    const isTextField = ae.tagName === "TEXTAREA" || ae.tagName === "INPUT" || ae.isContentEditable === true;
    if (!isTextField)
        return null;
    return cardListEl.contains(ae) ? ae : null;
}
export function rerenderCardList() {
    updateCardsPaneTitle();
    const cardListEl = editPaneBody?.querySelector(".editor-card-list");
    if (!cardListEl)
        return;
    const activeField = _focusedEditableInCardList(cardListEl);
    if (activeField) {
        // Defer the rebuild until the field loses focus, then re-run (a deferred
        // rerenderCardList re-checks focus, so tabbing between two card fields keeps
        // deferring). One-shot + armed-guard so a burst of keystroke-ops doesn't
        // stack listeners. The setTimeout(0) lets the synchronous focusout→focusin
        // pair settle so the re-check sees the NEW focus target, not the transient
        // <body> the browser parks focus on mid-transition.
        if (!_cardListReconcileArmed) {
            _cardListReconcileArmed = true;
            activeField.addEventListener("blur", () => {
                _cardListReconcileArmed = false;
                setTimeout(() => rerenderCardList(), 0);
            }, { once: true });
        }
        return;
    }
    const selectStruct = _requireSelectStruct();
    renderCardList({
        container: cardListEl,
        cards: state.cards,
        expandedIds: state.expandedIds,
        selectedIds: state.selectedStructIds,
        getSelectedIds: () => state.selectedStructIds || new Set(),
        onSelect: (id, ev) => {
            const mode = modeFromEvent(ev);
            if (state.linkedViewsCtrl) {
                state.linkedViewsCtrl.selectStructId(id, mode);
                if (mode === "single") {
                    const card = state.cardByStructId.get(id);
                    state.linkedViewsCtrl.scrollOverlayIntoView(id, card ? card.page : undefined);
                    state.linkedViewsCtrl.flashOverlay(id);
                }
            }
            if (mode === "single")
                selectStruct(id);
        },
        onOp: (op) => { void applyPowerEditorOp(op); },
        onExpand: (id) => {
            // Single-expand: collapse all others to keep the surface tidy.
            state.expandedIds.clear();
            state.expandedIds.add(id);
            if (state.linkedViewsCtrl)
                state.linkedViewsCtrl.selectStructId(id, "single");
            rerenderCardList();
        },
        onCollapse: (id) => {
            state.expandedIds.delete(id);
            rerenderCardList();
        },
        onJump: (id) => {
            const card = state.cardByStructId.get(id);
            if (state.linkedViewsCtrl) {
                state.linkedViewsCtrl.selectStructId(id, "single");
                state.linkedViewsCtrl.scrollOverlayIntoView(id, card ? card.page : undefined);
                state.linkedViewsCtrl.flashOverlay(id);
            }
        },
        // Page-section header "Scroll to page" jump. Routes through the
        // multi-page manager when present; falls back to renderPage when
        // the manager is absent (unit tests / pre-init).
        onJumpToPage: (pageNum) => {
            if (!pageNum)
                return;
            if (state.multiPageMgr && typeof state.multiPageMgr.scrollToPage === "function") {
                void state.multiPageMgr.scrollToPage(pageNum);
                return;
            }
            if (state.pdfDoc && pageNum !== state.currentPage) {
                // eslint-disable-next-line promise/prefer-await-to-then -- fire-and-forget in sync onJumpToPage callback
                void renderPage(pageNum).catch(() => { });
            }
        },
        onReorderClick: (structId, dir) => nudgeReadingOrder(structId, dir),
        onReorder: (movedIds, beforeId) => {
            // movedIds may contain 1+ struct ids (group drag). Backward-
            // compat with the older single-string callers: coerce to array.
            const ids = Array.isArray(movedIds) ? movedIds.slice() : [movedIds];
            if (ids.length === 0)
                return;
            const moved = state.cardByStructId.get(ids[0]); // WHY: ids.length === 0 early-return above
            if (!moved)
                return;
            // Within-page reorder only. All selected cards on the moved
            // card's page move together.
            const pageCards = state.cards.filter((c) => c.page === moved.page);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- WHY: c.structId is any-typed in card state; full typing is a separate effort
            const ordered = pageCards.map((c) => c.structId);
            const before = ordered.slice();
            const movedSet = new Set(ids);
            const without = ordered.filter((id) => !movedSet.has(id));
            // If the drop target is on a different page or off the end,
            // append to the end of the moved card's page.
            const insertIdx = beforeId && without.includes(beforeId)
                ? without.indexOf(beforeId)
                : without.length;
            // Insert all moved ids in their card-list order at insertIdx.
            const movedOrdered = ordered.filter((id) => movedSet.has(id));
            without.splice(insertIdx, 0, ...movedOrdered);
            const after = without;
            // No-op if order unchanged.
            if (before.length === after.length && before.every((v, i) => v === after[i]))
                return;
            void applyPowerEditorOp({
                kind: "reorder-reading",
                pageNum: moved.page,
                before,
                after,
            });
        },
    });
    // Refresh selection classes after the DOM is rebuilt.
    if (state.linkedViewsCtrl && typeof state.linkedViewsCtrl.applyClasses === "function") {
        state.linkedViewsCtrl.applyClasses();
    }
}
/**
 * Nudge a single element ±1 step in the page-local reading order. Used by
 * the upper-left order-chip arrows on each overlay box AND by the matching
 * up/down buttons on cards (single dispatch path so card-pane and overlay
 * stay in lockstep). Wraps the existing `reorder-reading` op which the IR
 * reducer + WASM applier already understand.
 *
 * @param structId  the element being moved
 * @param dir       "up" = earlier in reading order; "down" = later
 */
export function nudgeReadingOrder(structId, dir) {
    if (!state.cardByStructId)
        return;
    const card = state.cardByStructId.get(structId);
    if (!card)
        return;
    const pageCards = (state.cards || []).filter((c) => c.page === card.page);
    const ordered = pageCards.map((c) => c.structId);
    const idx = ordered.indexOf(structId);
    if (idx < 0)
        return;
    const nextIdx = dir === "up" ? idx - 1 : idx + 1;
    if (nextIdx < 0 || nextIdx >= ordered.length)
        return;
    const before = ordered.slice();
    const after = ordered.slice();
    // WHY: idx is bounds-checked (idx < 0 early-return above); nextIdx is bounds-checked (< 0 || >= length guard above)
    [after[idx], after[nextIdx]] = [after[nextIdx], after[idx]];
    void applyPowerEditorOp({
        kind: "reorder-reading",
        pageNum: card.page,
        before,
        after,
    });
}
/**
 * Update the Save button appearance when pending inserts exist.
 * Amber tint + tooltip when blocked; normal when clear.
 *
 * Routes through setSaveBtnState so the full attribute set
 * (label + classes + disabled + title + aria-label) lands as a unit.
 * The pre-fix implementation flipped only the `is-save-blocked` class
 * + title + aria-label, leaving the disabled/label fields drift-prone
 * across the 5 save-related call sites.
 *
 * EMPTY-state takes precedence: when no PDF is loaded the button is
 * disabled regardless of pending-inserts count.
 */
export function updateSaveBlockUI() {
    if (!els.saveBtn)
        return;
    // EMPTY state pre-empts BLOCKED — no PDF means there's nothing to save.
    if (!state.lifecycle.isAny([
        EditorLifecycleState.LOADED_TIER1,
        EditorLifecycleState.LOADED_FULL,
    ])) {
        setSaveBtnState(els.saveBtn, SaveBtnState.EMPTY);
        return;
    }
    const { blocked, message } = saveBlockState(state.pendingInsertStore);
    if (blocked) {
        setSaveBtnState(els.saveBtn, SaveBtnState.BLOCKED, { blockMessage: message });
    }
    else {
        setSaveBtnState(els.saveBtn, SaveBtnState.READY);
    }
}
